import toast from "react-hot-toast";
import ModalWrapper from "../modalWrapper/ModalWrapper";
import { ActionBtn } from "../buttons/actionBtn/ActionBtn";
import { InputElement } from "../inputElement/InputElement";
import { inviteDriver } from "../../redux/driver/thunks/inviteDriverThunk";
import { updateDriver } from "../../redux/driver/thunks/updateDriverThunk";
import { fetchDrivers } from "../../redux/driver/thunks/getDriverThunk";
import { fetchVehicles } from "../../redux/vehicle/thunks/getVehicleThunk";
import { SelectElement } from "../selectElement/SelectElement";
import { LoadingSpinner } from "../loadingSpinner/LoadingSpinner";
import { useTranslations } from "../../helperFunctions/customHooks/useTranslations";
import { useStatusEffect } from "../../helperFunctions/customHooks/useStatusEffect";
import { useEffect, useRef } from "react";
import { validateRequiredFields } from "../../helperFunctions/validateRequiredFields";
import { resetDriverInviteStatus } from "../../redux/driver/slices/inviteDriverSlice";
import { resetUpdateDriverStatus } from "../../redux/driver/slices/updateDriverSlice";
import { useDispatch, useSelector } from "react-redux";
import { resetDriverForm, setDriverForm } from "../../redux/forms/driverForm";
import {
  toggleAddNewDriverModal,
  setSelectedDataForEdit,
} from "../../redux/globalFunctions/globalFnSlice";

export function AddNewDriver() {
  const { isEditMode, selectedDataForEdit } = useSelector(
    (state) => state.global
  );
  const formData = useSelector((state) => state.forms.driverForm);

  const dispatch = useDispatch();

  const hasMounted = useRef(false);

  const { t } = useTranslations();
  const modalText = t.addDriverModal;
  const validationText = t.formValidations;

  const updateDriverState = useSelector((state) => state.updateDriver);
  const inviteDriverState = useSelector((state) => state.inviteDriver);
  const { vehicles } = useSelector((state) => state.vehicles);

  useEffect(() => {
    dispatch(fetchVehicles());
  }, [dispatch]);

  useEffect(() => {
    hasMounted.current = true;
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;

    let newValue = value;

    // If the field is "phone", allow only digits and a single '+' at the start
    if (name === "phone") {
      newValue = newValue.replace(/[^0-9+]/g, "");

      if (newValue.length > 1 && newValue[0] === "+") {
        newValue = "+" + newValue.slice(1).replace(/\+/g, "");
      } else {
        newValue = newValue.replace(/\+/g, "");
      }
    }

    // Update form data
    dispatch(setDriverForm({ [name]: newValue }));
  };

  // Populate the fields if is in edit mode, if its not then fields are empty.
  useEffect(() => {
    if (isEditMode && selectedDataForEdit) {
      dispatch(
        setDriverForm({
          firstName: selectedDataForEdit.u_firstname || "",
          lastName: selectedDataForEdit.u_lastname || "",
          phone: selectedDataForEdit.u_phonenumber || "",
          vehicle: selectedDataForEdit.u_vehicle_id || "",
        })
      );
    }
  }, [isEditMode, selectedDataForEdit]);

  useStatusEffect({
    status: updateDriverState.status,
    successMessage: modalText.successUpdating,
    errorMessage: modalText.errorUpdating + "." + " " + updateDriverState.error,
    onSuccess: toggleAddNewDriverModal,
    reFetch: fetchDrivers,
    resetStatus: resetUpdateDriverStatus,
    onError: toggleAddNewDriverModal,
    resetForm: resetDriverForm,
  });

  useStatusEffect({
    status: inviteDriverState.status,
    successMessage: modalText.successCreated,
    errorMessage: modalText.errorCreating + "." + " " + inviteDriverState.error,
    onSuccess: toggleAddNewDriverModal,
    resetStatus: resetDriverInviteStatus,
    onError: toggleAddNewDriverModal,
    reFetch: fetchDrivers,
    resetForm: resetDriverForm,
  });

  const handleFormSubmit = () => {
    const requiredFields = isEditMode
      ? ["firstName", "lastName", "phone"]
      : ["phone", "email"];

    const errors = validateRequiredFields(
      formData,
      requiredFields,
      validationText
    );

    if (errors.length > 0) {
      toast.dismiss();
      setTimeout(() => {
        errors.forEach((err) =>
          toast.error(err, {
            duration: 2000,
          })
        );
      }, 100);
      return;
    }

    const driverData = {
      phonenumber: formData.phone,
      email: formData.email,
    };

    if (isEditMode) {
      dispatch(
        updateDriver({
          u_id: selectedDataForEdit.u_id,
          u_firstname: formData.firstName,
          u_lastname: formData.lastName,
          ...(formData.vehicle ? { u_vehicle_id: formData.vehicle } : {}),
          u_phonenumber: formData.phone,
        })
      );
    } else {
      dispatch(inviteDriver(driverData));
    }
  };

  const isStatusLoading =
    inviteDriverState.status === "loading" ||
    updateDriverState.status === "loading";

  function handleCloseModal() {
    if (isEditMode) {
      dispatch(resetDriverForm());
    }
    dispatch(toggleAddNewDriverModal());
    dispatch(setSelectedDataForEdit(""));
  }

  const headerText = isEditMode ? modalText.editText : modalText.addText;

  // Prevent space from being added.
  const handleKeyPress = (e) => {
    if (e.key === " ") {
      e.preventDefault();
    }
  };

  function handleResetForm() {
    dispatch(resetDriverForm());
  }

  const resetButton = [
    <ActionBtn
      text={t.addDeliveryModal.reset}
      onClick={handleResetForm}
      disabled={isEditMode}
    />,
  ];

  return (
    <ModalWrapper
      onClick={handleCloseModal}
      title={headerText}
      onSubmit={handleFormSubmit}
      resetButton={resetButton}
    >
      {isEditMode && (
        <>
          <InputElement
            name={"firstName"}
            type={"text"}
            id={"firstname"}
            labelText={modalText.form.firstname}
            value={formData.firstName}
            onChange={handleChange}
          />

          <InputElement
            name={"lastName"}
            type={"text"}
            id={"lastname"}
            labelText={modalText.form.lastname}
            value={formData.lastName}
            onChange={handleChange}
          />
          <SelectElement
            maxWidth={"100%"}
            id="vehicle"
            name="vehicle"
            onChange={handleChange}
            value={formData.vehicle || ""}
            options={vehicles}
            optionValueKey="v_id"
            optionLabelKey="v_name"
          />
        </>
      )}
      <InputElement
        name={"phone"}
        type={"tel"}
        id={"phone"}
        labelText={modalText.form.phone}
        value={formData.phone}
        onChange={handleChange}
      />
      {!isEditMode && (
        <InputElement
          name={"email"}
          type={"email"}
          id={"email"}
          labelText={modalText.form.email}
          value={formData.email}
          onChange={handleChange}
          onKeyDown={handleKeyPress}
        />
      )}
      <ActionBtn
        width={"100%"}
        height={"42px"}
        disabled={isStatusLoading}
        onClick={handleFormSubmit}
        text={isStatusLoading ? <LoadingSpinner /> : modalText.saveBtn}
      />
    </ModalWrapper>
  );
}
